import type { LucideIcon } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Check } from "@/components/icons";
import type { TranslationKey } from "@/i18n";
import { cn } from "@/lib/utils";

export interface StepperStep {
  id: string;
  labelKey: TranslationKey;
  icon: LucideIcon;
}

/**
 * Icon stepper for the buyer setup wizard's guided first run: one node per
 * step with its name underneath, connected by a fill line. On phones only
 * the current step's name fits, so the others hide and a "Step n of N"
 * line takes over. Past nodes are tappable to jump back; future nodes are
 * not (no skipping ahead).
 */
export function Stepper({
  steps,
  current,
  onJump,
}: {
  steps: StepperStep[];
  current: number;
  onJump?: (index: number) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="space-y-2">
      <div className="flex items-start">
        {steps.map((step, i) => {
          const Icon = step.icon;
          const isPast = i < current;
          const isCurrent = i === current;
          const clickable = isPast && !!onJump;
          return (
            <div
              key={step.id}
              className="relative flex min-w-0 flex-1 basis-0 flex-col items-center"
            >
              {i > 0 && (
                <span
                  aria-hidden
                  className={cn(
                    "absolute left-[calc(-50%+20px)] right-[calc(50%+20px)] top-4 h-0.5 rounded-full transition-colors",
                    i <= current ? "bg-primary" : "bg-muted",
                  )}
                />
              )}
              <button
                type="button"
                disabled={!clickable}
                onClick={clickable ? () => onJump?.(i) : undefined}
                aria-current={isCurrent ? "step" : undefined}
                aria-label={t(step.labelKey)}
                className={cn(
                  "flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-2 transition-colors",
                  isPast && "border-primary bg-primary text-primary-foreground",
                  isCurrent && "border-primary bg-primary/10 text-primary",
                  !isPast && !isCurrent && "border-transparent bg-muted text-muted-foreground",
                  clickable ? "cursor-pointer" : "cursor-default",
                )}
              >
                {isPast ? <Check className="h-4 w-4" /> : <Icon className="h-4 w-4" />}
              </button>
              <span
                className={cn(
                  "mt-1.5 w-full text-center text-xs leading-tight",
                  isCurrent
                    ? "font-medium text-foreground"
                    : "hidden text-muted-foreground sm:block",
                )}
              >
                {t(step.labelKey)}
              </span>
            </div>
          );
        })}
      </div>
      <p className="text-center text-xs text-muted-foreground sm:hidden">
        {t("setup.stepOf", { current: current + 1, total: steps.length })}
      </p>
    </div>
  );
}
